import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Customer-portal auth state — the third, intentionally-disjoint auth store
 * (alongside useAuthStore for staff and usePlatformAuthStore for platform
 * admins). Distinct sessionStorage key so all three can coexist in one browser.
 */

interface PortalCustomer {
  id: string;
  /** Phone is the login identity; email is legacy and may be null. */
  phone: string | null;
  email: string | null;
  partyId: string;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
}

interface CustomerAuthState {
  accessToken: string | null;
  expiresAt: number | null;
  customer: PortalCustomer | null;
  setSession(p: { accessToken: string; expiresIn: number; customer: PortalCustomer }): void;
  setToken(token: string, expiresIn: number): void;
  clear(): void;
}

export const useCustomerAuthStore = create<CustomerAuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      expiresAt: null,
      customer: null,
      setSession: ({ accessToken, expiresIn, customer }) =>
        set({ accessToken, expiresAt: Date.now() + expiresIn * 1000, customer }),
      setToken: (accessToken, expiresIn) =>
        set({ accessToken, expiresAt: Date.now() + expiresIn * 1000 }),
      clear: () => set({ accessToken: null, expiresAt: null, customer: null }),
    }),
    {
      name: 'erp-customer-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export type { PortalCustomer };
